import { useEffect, useState, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useTheme } from "../../contexts/ThemeContext";
import { useAuth } from "../../contexts/AuthContext";

const SUPABASE_FUNCTIONS_URL = import.meta.env.VITE_PUBLIC_SUPABASE_URL
  ? `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1`
  : "";

const PRO_FEATURES = [
  { icon: "ri-bar-chart-2-line", label: "Performance Dashboard" },
  { icon: "ri-line-chart-line", label: "ROI Analytics" },
  { icon: "ri-history-line", label: "Full Flip History & Log" },
  { icon: "ri-file-excel-2-line", label: "CSV Export" },
  { icon: "ri-upload-cloud-2-line", label: "Bulk Flip Import" },
  { icon: "ri-customer-service-2-line", label: "Priority Support" },
];

type VerifyStatus = "idle" | "loading" | "success" | "error";

export default function CheckoutSuccessPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { isDark } = useTheme();
  const { user, session, loading, refreshProfile } = useAuth();

  const sessionId = searchParams.get("session");

  const [status, setStatus] = useState<VerifyStatus>("idle");
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [billingPeriod, setBillingPeriod] = useState<"monthly" | "lifetime" | null>(null);
  const [countdown, setCountdown] = useState(10);
  const verifyAttempted = useRef(false);

  // Wait for auth to load, then verify
  useEffect(() => {
    if (loading) return;

    // Not logged in — bounce to dashboard with session ID preserved so it can verify after login
    if (!user || !session) {
      const dest = sessionId
        ? `/dashboard?checkout=success&session=${sessionId}`
        : "/dashboard";
      navigate(dest, { replace: true });
      return;
    }

    // No session ID — nothing to verify
    if (!sessionId) {
      navigate("/dashboard", { replace: true });
      return;
    }

    if (verifyAttempted.current) return;
    verifyAttempted.current = true;

    setStatus("loading");

    fetch(`${SUPABASE_FUNCTIONS_URL}/verify-stripe-session`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${session.access_token}`,
      },
      body: JSON.stringify({ sessionId }),
    })
      .then((r) => r.json())
      .then(async (data) => {
        if (data.success) {
          await refreshProfile();
          setBillingPeriod(data.billing_period ?? null);
          setStatus("success");
        } else {
          setErrorMsg(data.error ?? "Payment verification failed.");
          setStatus("error");
        }
      })
      .catch(() => {
        setErrorMsg("Could not connect to our servers. Your payment may still be confirmed — check your dashboard.");
        setStatus("error");
      });
  }, [loading, user, session, sessionId, navigate, refreshProfile]);

  // Auto-redirect countdown after success
  useEffect(() => {
    if (status !== "success") return;
    if (countdown <= 0) {
      navigate("/dashboard", { replace: true });
      return;
    }
    const t = setTimeout(() => setCountdown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [status, countdown, navigate]);

  const bg = isDark ? "bg-[#050505]" : "bg-white";
  const cardBg = isDark ? "bg-[#0d0d0d] border-white/6" : "bg-[#f8f8f8] border-black/6";
  const textPrimary = isDark ? "text-white" : "text-[#111]";
  const textMuted = isDark ? "text-[#555]" : "text-[#888]";
  const divider = isDark ? "border-white/6" : "border-black/6";

  return (
    <div className={`min-h-screen ${bg} flex flex-col items-center justify-center px-6 py-16 transition-colors duration-300`}>

      {/* ── Loading state ── */}
      {(status === "idle" || status === "loading") && (
        <div className="flex flex-col items-center gap-5 text-center">
          <div className={`w-16 h-16 rounded-2xl flex items-center justify-center border ${cardBg}`}>
            <i className={`ri-loader-4-line animate-spin text-2xl ${isDark ? "text-white/30" : "text-black/20"}`} />
          </div>
          <div>
            <p className={`font-syne font-semibold text-lg mb-1 ${textPrimary}`}>Confirming your payment…</p>
            <p className={`font-inter text-sm ${textMuted}`}>Just a moment while we verify with Stripe.</p>
          </div>
        </div>
      )}

      {/* ── Error state ── */}
      {status === "error" && (
        <div className="w-full max-w-md text-center">
          <div className={`w-16 h-16 rounded-2xl flex items-center justify-center border mx-auto mb-6 ${isDark ? "bg-red-500/10 border-red-500/20" : "bg-red-50 border-red-100"}`}>
            <i className={`ri-error-warning-line text-2xl ${isDark ? "text-red-400" : "text-red-500"}`} />
          </div>
          <h1 className={`font-syne font-bold text-2xl mb-3 ${textPrimary}`}>Something went wrong</h1>
          <p className={`font-inter text-sm leading-relaxed mb-8 ${textMuted}`}>
            {errorMsg}
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <button
              type="button"
              onClick={() => navigate("/dashboard")}
              className={`px-6 py-3 rounded-xl font-inter text-sm font-medium whitespace-nowrap cursor-pointer transition-all duration-200 ${
                isDark ? "bg-white text-black hover:bg-[#e0e0e0]" : "bg-[#111] text-white hover:bg-[#333]"
              }`}
            >
              <i className="ri-dashboard-3-line mr-2 text-xs" />
              Go to Dashboard
            </button>
            <a
              href="mailto:support@trackly.app"
              className={`px-6 py-3 rounded-xl font-inter text-sm font-medium whitespace-nowrap cursor-pointer transition-all duration-200 border ${
                isDark ? "border-white/10 text-[#888] hover:border-white/20 hover:text-white" : "border-black/10 text-[#666] hover:border-black/20 hover:text-[#111]"
              }`}
            >
              <i className="ri-mail-line mr-2 text-xs" />
              Contact Support
            </a>
          </div>
        </div>
      )}

      {/* ── Success state ── */}
      {status === "success" && (
        <div className="w-full max-w-lg">

          {/* Crown icon with glow */}
          <div className="flex justify-center mb-8">
            <div className="relative">
              <div className={`absolute inset-0 rounded-3xl blur-2xl opacity-30 ${isDark ? "bg-amber-400" : "bg-amber-300"}`} />
              <div className={`relative w-20 h-20 rounded-3xl flex items-center justify-center border ${isDark ? "bg-amber-500/15 border-amber-500/30" : "bg-amber-50 border-amber-200"}`}>
                <i className={`ri-vip-crown-fill text-4xl ${isDark ? "text-amber-400" : "text-amber-500"}`} />
              </div>
            </div>
          </div>

          {/* Headline */}
          <div className="text-center mb-8">
            <div className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full font-inter text-xs font-medium mb-4 border ${isDark ? "bg-amber-500/12 border-amber-500/25 text-amber-400" : "bg-amber-50 border-amber-200 text-amber-600"}`}>
              <i className="ri-checkbox-circle-fill text-[11px]" />
              Payment confirmed
            </div>
            <h1 className={`font-syne font-bold text-4xl mb-3 ${textPrimary}`}>
              Welcome to<br />
              <span className={isDark ? "text-amber-400" : "text-amber-500"}>Trackly Pro.</span>
            </h1>
            <p className={`font-inter text-sm leading-relaxed ${textMuted}`}>
              {billingPeriod === "lifetime"
                ? "You've got lifetime access — pay once, use forever. Every feature, yours for good."
                : "Your monthly Pro membership is active. Everything unlocks the moment we launch."}
            </p>
          </div>

          {/* Plan summary card */}
          <div className={`rounded-2xl border p-5 mb-5 ${cardBg}`}>
            <div className="flex items-center justify-between mb-4 pb-4 border-b ${divider}">
              <div className="flex items-center gap-3">
                <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ${isDark ? "bg-amber-500/15" : "bg-amber-50"}`}>
                  <i className={`ri-vip-crown-line text-sm ${isDark ? "text-amber-400" : "text-amber-500"}`} />
                </div>
                <div>
                  <p className={`font-inter text-sm font-semibold ${textPrimary}`}>Trackly Pro</p>
                  <p className={`font-inter text-xs ${textMuted}`}>
                    {billingPeriod === "lifetime" ? "Lifetime access — one-time payment" : "Monthly subscription"}
                  </p>
                </div>
              </div>
              <div className="text-right">
                <p className={`font-syne font-bold text-lg ${isDark ? "text-amber-400" : "text-amber-500"}`}>
                  {billingPeriod === "lifetime" ? "$200" : "$10"}
                </p>
                <p className={`font-inter text-[10px] ${textMuted}`}>
                  {billingPeriod === "lifetime" ? "once" : "/ month"}
                </p>
              </div>
            </div>

            {/* Features unlocked */}
            <p className={`font-inter text-[10px] uppercase tracking-widest mb-3 ${isDark ? "text-[#444]" : "text-[#bbb]"}`}>
              Everything you just unlocked
            </p>
            <div className="grid grid-cols-2 gap-2">
              {PRO_FEATURES.map((f) => (
                <div key={f.label} className="flex items-center gap-2">
                  <div className={`w-5 h-5 flex items-center justify-center flex-shrink-0`}>
                    <i className={`${f.icon} text-xs ${isDark ? "text-amber-400/70" : "text-amber-500"}`} />
                  </div>
                  <span className={`font-inter text-xs ${isDark ? "text-[#777]" : "text-[#666]"}`}>{f.label}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Launch note */}
          <div className={`rounded-xl border px-4 py-3.5 mb-6 flex items-start gap-2.5 ${isDark ? "border-white/5 bg-white/2" : "border-black/5 bg-black/2"}`}>
            <i className={`ri-time-line text-sm mt-px flex-shrink-0 ${textMuted}`} />
            <p className={`font-inter text-xs leading-relaxed ${textMuted}`}>
              Features activate automatically when Trackly launches into early access. You&#39;ll get an email the moment it&apos;s live.
            </p>
          </div>

          {/* CTA */}
          <div className="flex flex-col items-center gap-3">
            <button
              type="button"
              onClick={() => navigate("/dashboard")}
              className={`w-full flex items-center justify-center gap-2 py-3.5 rounded-xl font-inter text-sm font-medium cursor-pointer transition-all duration-200 ${
                isDark
                  ? "bg-white text-black hover:bg-[#e0e0e0]"
                  : "bg-[#111] text-white hover:bg-[#333]"
              }`}
            >
              <i className="ri-dashboard-3-line text-xs" />
              Go to Dashboard
            </button>
            <p className={`font-inter text-xs ${isDark ? "text-[#333]" : "text-[#ccc]"}`}>
              Redirecting automatically in {countdown}s
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
